import { Applicant, DecisionAction } from "shared/types";
import { FormData, Views } from "./types";
import { AdverseActionLetterType } from "api/landlord/templates";
import { Template } from "types/landlord/verificationTemplate";
import { flows } from "./constants";
import moment from "moment";

const INDENT = "\u2003 ";

interface AdverseActionNoticeParams {
  applicantName: string;
  address: string;
  cityStateZip: string;
  isRejected: boolean;
  isConditionallyApproved: boolean;
  residencyChecks: Array<{ id: string; text: string; checked: boolean }>;
  additionalResidencyCondition?: string;
  adverseChecks: Array<{ id: string; text: string; checked: boolean }>;
  additionalCondition?: string;
  currentDate: string;
}

const buildAdverseActionNotice = (
  params: AdverseActionNoticeParams
): string => {
  const {
    applicantName,
    address,
    cityStateZip,
    isRejected,
    isConditionallyApproved,
    residencyChecks,
    additionalResidencyCondition,
    adverseChecks,
    additionalCondition,
    currentDate
  } = params;

  return `ADVERSE ACTION NOTICE
Name: ${applicantName}
Address: ${address}
City/State/Zip Code: ${cityStateZip}

This notice is to inform you that your application has been:
${isRejected ? "☒" : "☐"} Rejected
${isConditionallyApproved ? "☒" : "☐"} Approved with conditions:
${residencyChecks
  .map((check) => `${INDENT}${check.checked ? "☒" : "☐"} ${check.text}`)
  .join("\n")}
${INDENT}${additionalResidencyCondition ? "☒" : "☐"} Other: ${additionalResidencyCondition || ""}

Adverse Action on your application was based on the following:
${adverseChecks
  .map((check) => `${check.checked ? "☒" : "☐"} ${check.text}`)
  .join("\n")}
${additionalCondition ? `☒ ${additionalCondition}\n` : ""}
${currentDate}

`;
};

const generateWATemplate = (
  formData: FormData,
  applicant: Applicant,
  residencyChecks: Array<{ id: string; text: string }>,
  adverseChecks: Array<{ id: string; text: string }>
) => {
  const address = applicant.housing_addresses?.[0]?.address1 || "";
  const cityStateZip = [
    applicant.housing_addresses?.[0]?.city,
    applicant.housing_addresses?.[0]?.state,
    applicant.housing_addresses?.[0]?.zip
  ]
    .filter(Boolean)
    .join(", ");

  const getCheckmark = (condition: string) =>
    formData.conditions.includes(condition);
  const getResidencyCheckmark = (condition: string) =>
    formData.residencyConditions?.includes(condition) || false;

  const currentDate = `Dated this ${moment().format("Do [day of] MMMM, YYYY")}`;

  return buildAdverseActionNotice({
    applicantName: `${applicant.first_name} ${applicant.last_name}`,
    address,
    cityStateZip,
    isRejected: formData.decision === DecisionAction.Decline,
    isConditionallyApproved:
      formData.decision === DecisionAction.ConditionalApprove,
    residencyChecks: residencyChecks.map((check) => ({
      ...check,
      checked: getResidencyCheckmark(check.id)
    })),
    additionalResidencyCondition: formData.additionalResidencyCondition,
    adverseChecks: adverseChecks.map((check) => ({
      ...check,
      checked: getCheckmark(check.id)
    })),
    additionalCondition: formData.additionalCondition,
    currentDate
  });
};

const generateGenericTemplate = (
  formData: FormData,
  address: string,
  applicationConditions: Array<{ id: string; text: string }>
) => {
  const conditionMap = new Map(
    applicationConditions.map((c) => [c.id, c.text])
  );

  const conditions = [
    ...formData.conditions.map((id) => conditionMap.get(id)),
    formData.additionalCondition
  ]
    .reduce<string[]>((acc, text) => {
      if (!text) return acc;
      const formatted = `• ${text}`;
      if (!acc.includes(formatted)) acc.push(formatted);
      return acc;
    }, [])
    .join("\n");

  const base = `We${formData.decision === DecisionAction.Decline ? " are sorry" : "'re excited"} to inform you that your application for ${address} has been`;

  switch (formData.decision) {
    case DecisionAction.Approve:
      return `${base} approved.`;
    case DecisionAction.ConditionalApprove:
      return `${base} approved.\n\nReason(s) for applying conditions:\n${conditions}`;
    default:
      return `${base} declined for the following reasons:\n\n${conditions}`;
  }
};

export const generateAdditionalEmailText = (
  formData: FormData,
  applicants: Applicant[],
  address: string,
  applicationConditions: Array<{ id: string; text: string }>,
  adverseActionTemplate: AdverseActionLetterType,
  conditionalApprovalEmailMessage?: string | null
) => {
  // Use partner's custom conditional approval message if available (already rendered by BE)
  if (
    formData.decision === DecisionAction.ConditionalApprove &&
    conditionalApprovalEmailMessage
  ) {
    const conditionMap = new Map(
      applicationConditions.map((c) => [c.id, c.text])
    );

    const conditions = [
      ...formData.conditions.map((id) => conditionMap.get(id)),
      formData.additionalCondition
    ]
      .reduce<string[]>((acc, text) => {
        if (!text) return acc;
        const formatted = `• ${text}`;
        if (!acc.includes(formatted)) acc.push(formatted);
        return acc;
      }, [])
      .join("\n");

    // Message already has placeholders replaced by BE, just append conditions
    let message = conditionalApprovalEmailMessage;

    if (conditions) message += `\n\n${conditions}`;

    return message;
  }
  if (
    adverseActionTemplate === AdverseActionLetterType.Washington &&
    formData.decision !== DecisionAction.Approve
  ) {
    const residencyChecks = [
      {
        id: "increased_deposit",
        text: "Residency requires an increased deposit"
      },
      {
        id: "qualified_guarantors",
        text: "Residency requires a qualified guarantor"
      },
      { id: "last_months_rent", text: "Residency requires last month's rent" },
      {
        id: "increased_monthly_rent",
        text: `Residency requires an increased monthly rent of $${formData.increasedMonthlyRent || "_____"}`
      }
    ];

    const adverseChecks = [
      {
        id: "information_contained_in_a_consumer_report",
        text: "Information contained in a consumer report (The prospective landlord must include the name, address, and phone number of the consumer reporting agency that furnished the consumer report that contributed to the Adverse Action.)"
      },
      {
        id: "the_consumer_credit_report_did_not_contain_sufficient_information",
        text: "The consumer credit report did not contain sufficient information"
      },
      {
        id: "information_received_from_previous_rental_history_or_reference",
        text: "Information received from previous rental history or reference"
      },
      {
        id: "information_received_in_a_criminal_record",
        text: "Information received in a criminal record"
      },
      {
        id: "information_received_in_a_civil_record",
        text: "Information received in a civil record"
      },
      {
        id: "information_received_from_an_employment_verification",
        text: "Information received from an employment verification"
      }
    ];

    return generateWATemplate(
      formData,
      applicants[0],
      residencyChecks,
      adverseChecks
    );
  }

  return generateGenericTemplate(formData, address, applicationConditions);
};

export const getFlow = (
  decision: DecisionAction | undefined,
  template?: Template,
  sendNotifications?: boolean
) => {
  let flow = flows[decision || DecisionAction.Approve];

  if (
    decision === DecisionAction.ConditionalApprove &&
    (!template?.adverse_action_letter_type ||
      template.adverse_action_letter_type === AdverseActionLetterType.Generic)
  )
    flow = flow.filter((view) => view !== Views.ResidencyConditions);

  if (!sendNotifications) flow = flow.filter((view) => view !== Views.Email);

  return flow;
};
